<!-- ══════════════════════════════════════════════════ -->
<!-- HORIZONTAL REELS BAR                              -->
<!-- ══════════════════════════════════════════════════ -->
<section class="reels-bar" [class.rtl]="rtl">

  <!-- Header row -->
  @if (title) {
    <div class="rb__header">
      <h2 class="rb__title">{{ title }}</h2>
      @if (reels.length > visibleCount) {
        <button
          class="rb__see-all"
          [attr.aria-expanded]="isExpanded"
          (click)="toggleSeeAll()">
          {{ rtl ? (isExpanded ? 'הצג פחות' : 'הצג הכל') : (isExpanded ? 'Show less' : 'See all') }}
          <span class="rb__see-all-arrow">{{ rtl ? '←' : '→' }}</span>
        </button>
      }
    </div>
  }

  <!-- Scroll area -->
  <div class="rb__wrap">
    @if (canScrollPrev) {
      <button
        class="rb__arrow rb__arrow--prev"
        (click)="scrollBarBy(-1)"
        [attr.aria-label]="rtl ? 'הקודם' : 'Previous'">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    }

    <div class="rb__track" #track dragScroll>
      @for (reel of displayedReels; track trackById(i, reel); let i = $index) {
        <div
          class="thumb"
          [class.thumb--viewing]="viewerOpen && activeIndex === i"
          (click)="openViewer(i)"
          role="button"
          tabindex="0"
          [attr.aria-label]="(rtl ? 'פתיחת סיפור: ' : 'Open story: ') + (reel | reelDisplay).title"
          (keydown.enter)="openViewer(i)"
          (keydown.space)="openViewer(i); $event.preventDefault()">
          <div class="thumb__media" [style.--accent]="(reel | reelDisplay).accentColor">
            <!-- Video thumbnail -->
            @if (reel.video_url) {
              <video
                class="thumb__vid"
                [src]="reel.video_url"
                [poster]="reel.images?.[0]?.image || ''"
                muted playsinline preload="none" loop
                (loadedmetadata)="onThumbMetadataLoaded($event, reel)"
                (mouseenter)="hoverPlay($event)"
                (mouseleave)="hoverPause($event)">
              </video>
            }
            <!-- Image-only thumbnail -->
            @if (!reel.video_url && reel.images?.[0]?.image) {
              <img
                class="thumb__img"
                [src]="reel.images![0].image"
                [alt]="(reel | reelDisplay).title"
                loading="lazy" />
            }
            <!-- No media -->
            @if (!reel.video_url && !reel.images?.length) {
              <div
                class="thumb__empty">
                <span class="mi">{{ (reel | reelDisplay).typeIcon }}</span>
              </div>
            }
            <!-- Play icon (video only) -->
            @if (reel.video_url) {
              <div class="thumb__play">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5.14v14l11-7-11-7z"/>
                </svg>
              </div>
            }
            <!-- Category badge -->
            <span class="thumb__badge" [style.background]="(reel | reelDisplay).accentColor + 'e6'">
              {{ (reel | reelDisplay).typeLabel }}
            </span>
            <!-- Price pill -->
            @if ((reel | reelDisplay).priceLine) {
              <span class="thumb__price">
                {{ (reel | reelDisplay).priceLine }}
              </span>
            }
          </div>
          <div class="thumb__info">
            <span class="thumb__name">{{ (reel | reelDisplay).title }}</span>
            <span class="thumb__loc">
              @if ((reel | reelDisplay).avgRating) {
                <span class="thumb__rating">
                  ★ {{ (reel | reelDisplay).avgRating | number:'1.1-1' }}
                </span>
              }
              <span class="thumb__loc-text">{{ (reel | reelDisplay).location || (reel | reelDisplay).subtitle }}</span>
            </span>
          </div>
        </div>
      }
    </div>

    @if (canScrollNext) {
      <button
        class="rb__arrow rb__arrow--next"
        (click)="scrollBarBy(1)"
        [attr.aria-label]="rtl ? 'הבא' : 'Next'">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    }
  </div>
</section>

<!-- ══════════════════════════════════════════════════ -->
<!-- FULLSCREEN VIEWER  (YouTube Shorts style)         -->
<!-- ══════════════════════════════════════════════════ -->
@if (viewerOpen) {
  <div
    class="vw"
    [class.rtl]="rtl"
    [class.vw--show]="viewerVisible"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="rtl ? 'מציג סיפורים' : 'Stories viewer'"
    (click)="closeViewer()">
    <div class="vw__shell" (click)="$event.stopPropagation()">
      <!-- ── Top bar ── -->
      <div class="vw__topbar">
        <button class="vw__btn" (click)="closeViewer()" [attr.aria-label]="rtl ? 'סגירה' : 'Close'">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="white" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <span class="vw__counter">{{ activeIndex + 1 }} / {{ reels.length }}</span>
        <button class="vw__btn" (click)="toggleMute()"
          [attr.aria-label]="rtl ? (isMuted ? 'הפעלת קול' : 'השתקת קול') : (isMuted ? 'Unmute' : 'Mute')">
          <!-- Volume on -->
          @if (!isMuted) {
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
            </svg>
          }
          <!-- Volume off -->
          @if (isMuted) {
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <line x1="23" y1="9" x2="17" y2="15"/>
              <line x1="17" y1="9" x2="23" y2="15"/>
            </svg>
          }
        </button>
      </div>
      <!-- ── Vertical snap-scroll ── -->
      <div class="vw__scroll" #viewerScroll (scroll)="onViewerScroll()">
        @for (reel of reels; track trackById(i, reel); let i = $index) {
          <div
            class="vw__slide"
            [attr.data-index]="i">
            <!-- ▸ Media layer -->
            @if (reel.video_url) {
              <video
                class="vw__video"
                [src]="reel.video_url"
                [poster]="reel.images?.[0]?.image || ''"
                playsinline loop
                [muted]="isMuted">
              </video>
            }
            @if (!reel.video_url && reel.images?.[0]?.image) {
              <img
                class="vw__poster"
                [src]="reel.images![0].image"
                [alt]="(reel | reelDisplay).title" />
            }
            @if (!reel.video_url && !reel.images?.length) {
              <div
                class="vw__no-media">
                <span class="mi xl">{{ (reel | reelDisplay).typeIcon }}</span>
              </div>
            }
            <!-- ▸ Tap to play/pause -->
            <div class="vw__tap" (click)="togglePlay(i)">
              @if (pausedSlide === i) {
                <div class="vw__paused-icon">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)">
                    <path d="M8 5.14v14l11-7-11-7z"/>
                  </svg>
                </div>
              }
            </div>
            <!-- ▸ Info overlay -->
            <div class="vw__info">
              <!-- Type chip -->
              <span
                class="vw__type-chip"
                [style.background]="(reel | reelDisplay).accentColor + 'cc'">
                <span class="mi sm">{{ (reel | reelDisplay).typeIcon }}</span>
                {{ (reel | reelDisplay).typeLabel }}
              </span>
              <!-- Title + subtitle -->
              <div class="vw__title-row">
                <span class="vw__title">{{ (reel | reelDisplay).title }}</span>
                @if ((reel | reelDisplay).subtitle) {
                  <span class="vw__subtitle">
                    {{ (reel | reelDisplay).subtitle }}
                  </span>
                }
              </div>
              <!-- Description (clamped) -->
              @if ((reel | reelDisplay).description) {
                <p class="vw__desc">
                  {{ (reel | reelDisplay).description }}
                </p>
              }
              <!-- Tags -->
              @if ((reel | reelDisplay).tags.length) {
                <div class="vw__tags">
                  @for (tag of (reel | reelDisplay).tags; track tag) {
                    <span class="vw__tag">
                      @if (tag.icon) {
                        <span class="mi xs">{{ tag.icon }}</span>
                      }
                      {{ tag.label }}
                    </span>
                  }
                </div>
              }
              <!-- Location -->
              @if ((reel | reelDisplay).location) {
                <div class="vw__location">
                  <span class="mi xs">location_on</span>
                  {{ (reel | reelDisplay).location }}
                </div>
              }
              <!-- Rating -->
              @if ((reel | reelDisplay).avgRating) {
                <div class="vw__rating">
                  <span class="vw__stars">
                    @for (star of [1,2,3,4,5]; track star) {
                      <span
                        class="vw__star"
                        [class.vw__star--filled]="star <= roundRating((reel | reelDisplay).avgRating)"
                        [class.vw__star--half]="star === roundRating((reel | reelDisplay).avgRating) + 1 && ((reel | reelDisplay).avgRating! % 1) >= 0.3"
                      >★</span>
                    }
                  </span>
                  <span class="vw__rating-text">
                    {{ (reel | reelDisplay).avgRating | number:'1.1-1' }}
                    ({{ (reel | reelDisplay).ratingsCount }})
                  </span>
                </div>
              }
              <!-- Price badge -->
              @if ((reel | reelDisplay).priceLine) {
                <div
                  class="vw__price"
                  [class.vw__price--free]="(reel | reelDisplay).priceIsFree"
                  [style.background]="(reel | reelDisplay).accentColor + 'dd'">
                  {{ (reel | reelDisplay).priceLine }}
                </div>
              }
            </div>
            <!-- ▸ Side actions -->
            <div class="vw__actions">
              <button
                class="vw__action"
                (click)="onLike.emit(reel); $event.stopPropagation()"
                [class.vw__action--loved]="reel.user_loves_it">
                <svg width="26" height="26" viewBox="0 0 24 24"
                  [attr.fill]="reel.user_loves_it ? 'red' : 'none'"
                  [attr.stroke]="reel.user_loves_it ? 'red' : 'white'"
                  stroke-width="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                <span [style.color]="reel.user_loves_it ? 'red' : 'white'">
                  {{ rtl ? 'לייק' : 'Like' }}
                </span>
              </button>
              <button class="vw__action" (click)="onShare.emit(reel); $event.stopPropagation()">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"/>
                </svg>
                <span>{{ rtl ? 'שיתוף' : 'Share' }}</span>
              </button>
              <button class="vw__action" (click)="onContact.emit(reel); $event.stopPropagation()">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>{{ rtl ? 'התקשר' : 'Call' }}</span>
              </button>
              <button class="vw__action" (click)="onWhatsapp.emit(reel); $event.stopPropagation()">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 0 1-4.243-1.214l-.253-.157-2.876.854.768-2.812-.17-.265A8 8 0 1 1 12 20z"/>
                </svg>
                <span>WhatsApp</span>
              </button>
            </div>
            <!-- ▸ Nav arrows (desktop) -->
            @if (i > 0) {
              <button aria-label="הקודם"
                class="vw__nav vw__nav--up"
                (click)="scrollToSlide(i - 1); $event.stopPropagation()">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M18 15l-6-6-6 6" stroke="white" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            }
            @if (i < reels.length - 1) {
              <button aria-label="הבא"
                class="vw__nav vw__nav--down"
                (click)="scrollToSlide(i + 1); $event.stopPropagation()">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="white" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            }
          </div>
        }
      </div>
      <!-- ── Progress dots (desktop) ── -->
      <div class="vw__dots">
        @for (r of reels; track r; let j = $index) {
          <span
            class="vw__dot"
            [class.vw__dot--on]="activeIndex === j"
            (click)="scrollToSlide(j); $event.stopPropagation()">
          </span>
        }
      </div>
    </div>
  </div>
}
